import { faCircleInfo, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, Tooltip } from "antd";
import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import {
  ENERGY_COMPONENT_TYPE_OPTIONS,
  LEVEL_OF_DETAIL_OPTIONS,
} from "../../constants/formEnums";
import type { EnergyComponent } from "../../types/formTypes";
import SingleSelectField from "./SingleSelectField";
import TextField from "./TextField";

const FieldWrapper = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const ComponentList = styled.div`
  margin-top: 6px;
`;

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item {
    margin-bottom: 8px;
  }
  .ant-collapse-header {
    align-items: center !important;
  }
  .ant-collapse-header button {
    width: auto !important;
    min-width: unset !important;
    flex: 0 0 auto !important;
    background: none !important;
    border: none !important;
    padding: 4px 8px !important;
  }
  .ant-collapse-content-box {
    padding: 12px 16px !important;
    width: 100%;
    box-sizing: border-box;
  }
`;

const ComponentFieldsWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;

  input,
  .ant-select {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const RemoveButton = styled.button`
  flex: 0 0 auto !important;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 4px 8px !important;
  color: var(--color-primary-desat) !important;
  font-size: 0.85rem;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto !important;
  min-width: auto !important;
  margin-left: auto;

  &:hover {
    color: #d32f2f !important;
  }
`;

const AddButton = styled.button`
  flex: 0 0 auto !important;
  margin-top: 12px;
  padding: 10px 40px !important;
  font-size: 0.9rem;
  border-radius: 6px;
  border: 1px dashed var(--color-primary-desat) !important;
  background: transparent !important;
  color: var(--color-primary) !important;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary) !important;
    background: rgba(0, 0, 0, 0.04) !important;
  }
`;

export interface EnergyComponentFieldProps {
  label: string;
  name: string;
  value: EnergyComponent[];
  onChange: (value: EnergyComponent[]) => void;
  required?: boolean;
  tooltip?: string;
  /** Label for each item in the list, e.g. "Component" */
  itemLabel?: string;
  /** Label for the add button, e.g. "+ Add Component" */
  addButtonLabel?: string;
}

const emptyComponent: EnergyComponent = {
  identifier: "",
  additionalType: undefined,
  name: "",
  levelOfDetail: undefined,
};

const EnergyComponentField: React.FC<EnergyComponentFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  tooltip,
  itemLabel = "Component",
  addButtonLabel = "+ Add Component",
}) => {
  const initialItems =
    value.length > 0 ? value : required ? [emptyComponent] : [];
  const [activeKeys, setActiveKeys] = useState<string[]>(() =>
    initialItems.map((_, i) => String(i)),
  );

  const items = value.length > 0 ? value : required ? [emptyComponent] : [];

  const updateItem = (index: number, updates: Partial<EnergyComponent>) => {
    const next = [...items];
    next[index] = { ...next[index], ...updates };
    onChange(next);
  };

  const addItem = () => {
    const next = [...items, { ...emptyComponent }];
    onChange(next);
    setActiveKeys((prev) => [...prev, String(next.length - 1)]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1 && required) return;
    const next = items.filter((_, i) => i !== index);
    onChange(next);
    setActiveKeys((prev) =>
      prev
        .filter((k) => k !== String(index))
        .map((k) =>
          parseInt(k, 10) > index ? String(parseInt(k, 10) - 1) : k,
        ),
    );
  };

  const collapseItems = items.map((item, index) => ({
    key: String(index),
    label: (
      <PanelHeader>
        <span>{itemLabel}</span>
        {(items.length > 1 || !required) && items.length >= 1 && (
          <RemoveButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeItem(index);
            }}
            aria-label={`Remove ${itemLabel.toLowerCase()}`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </RemoveButton>
        )}
      </PanelHeader>
    ),
    children: (
      <ComponentFieldsWrapper>
        <TextField
          label="Identifier"
          name={`${name}-${index}-identifier`}
          value={item.identifier ?? ""}
          onChange={(v) => updateItem(index, { identifier: v })}
          tooltip="The identifier property represents any kind of identifier for any kind of Thing, such as ISBNs, GTIN codes, UUIDs etc. Schema.org provides dedicated properties for representing many of these, either as Textual strings or as URL (URI) links. See background notes for more details."
        />
        <SingleSelectField
          label="Component Type"
          name={`${name}-${index}-additionalType`}
          value={item.additionalType}
          onChange={(v) => updateItem(index, { additionalType: v })}
          options={[...ENERGY_COMPONENT_TYPE_OPTIONS]}
          tooltip="Type of the component, e.g., pv"
        />
        <TextField
          label="Name"
          name={`${name}-${index}-name`}
          value={item.name ?? ""}
          onChange={(v) => updateItem(index, { name: v })}
          tooltip="Name of the component."
        />
        <SingleSelectField
          label="Level of Detail"
          name={`${name}-${index}-levelOfDetail`}
          value={item.levelOfDetail}
          onChange={(v) => updateItem(index, { levelOfDetail: v })}
          options={[...LEVEL_OF_DETAIL_OPTIONS]}
          tooltip="Level of detail of the component."
        />
      </ComponentFieldsWrapper>
    ),
  }));

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <ComponentList>
        <StyledCollapse
          activeKey={activeKeys}
          onChange={(keys) =>
            setActiveKeys(Array.isArray(keys) ? keys : [keys])
          }
          items={collapseItems}
        />
        <AddButton type="button" onClick={addItem}>
          {addButtonLabel}
        </AddButton>
      </ComponentList>
    </FieldWrapper>
  );
};

export default EnergyComponentField;
